/* Общая модель товарных экранов «Студии»: «Карточка для маркетплейса» (mcard), «Слайды с преимуществами» (infog)
   и «Карусель для поста» (post). APP-SPEC-5 §11, §13, §14, §17.

   Правило данных (docs/57-production-ui-audit.md): значения, ключи и цены — только из опубликованной формы
   GET /app/studio/apps/{id}/form. Здесь лежат лишь подписи к опубликованным значениям (словарь экранов из спецификации)
   и чистые функции. Контрол из спецификации появляется, только когда форма публикует его поле; ключ ищется
   по списку допустимых имён (docs/03-api.md §9.2 и плоские строковые варианты value_encoding: "string"). */
import type { StudioForm } from '../studio/studio-api';
import { record } from '../studio/studio-content';

export type Field = StudioForm['fields'][number];
export type ProductScreen = 'mcard' | 'infog' | 'post';

/** Допустимые имена ключей каждого контрола. Первый найденный в форме — тот, что уходит в заказ. */
export const KEYS = {
  text: ['text'],
  topic: ['topic', 'text'],
  name: ['name'],
  advantages: ['advantages'],
  outputs: ['outputs', 'pick'],
  photoCount: ['photo_count', 'count'],
  platform: ['platform', 'market'],
  wishes: ['wishes'],
  photoSet: ['photo_set', 'set_id', 'set'],
  slidesStyle: ['slides_style'],
  videoMotion: ['video_motion', 'motion_id', 'motion'],
  descTone: ['desc_tone', 'text_tone'],
  style: ['style_id', 'style'],
  slides: ['slides', 'slide_count', 'slides_count'],
  cover: ['clean_cover', 'cover'],
  specs: ['specs'],
  format: ['format'],
  tone: ['tone'],
} as const;
export type ControlKey = keyof typeof KEYS;

/** Поля контракта экрана (docs/59-studio-backend-contracts.md §1: `screen_fields[]`, value_encoding "json").
    Транспорт формы (use-studio-form → studio-command) пока передаёт только строки, поэтому берём лишь те поля
    экрана, чьё значение — строка; поля с числом, массивом или объектом ждут JSON-транспорта и не показываются. */
export function screenFields(form: StudioForm | null | undefined): Field[] {
  const raw = record(form).screen_fields;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(record)
    .filter(field => {
      const kind = field.json_type ?? field.value_type ?? record(field.schema).type;
      return (
        typeof field.key === 'string' &&
        ['enum', 'text'].includes(String(field.type)) &&
        !field.upload &&
        (kind === undefined || kind === 'string')
      );
    })
    .map(field => ({
      ...(field as unknown as Field),
      values: Array.isArray(field.values) ? field.values.filter((value): value is string => typeof value === 'string') : [],
      upload: null,
    }));
}
export function fieldOf(form: StudioForm | null | undefined, control: ControlKey): Field | undefined {
  const names = KEYS[control] as readonly string[];
  const pool = [...(form?.fields ?? []), ...screenFields(form)];
  for (const name of names) {
    const found = pool.find(field => field.key === name);
    if (found) return found;
  }
  return undefined;
}
/** Поле выбора годно, только если у него есть опубликованные значения. */
export const enumOf = (form: StudioForm | null | undefined, control: ControlKey) => {
  const field = fieldOf(form, control);
  return field && field.type === 'enum' && field.values.length && !field.upload ? field : undefined;
};
export const textOf = (form: StudioForm | null | undefined, control: ControlKey) => {
  const field = fieldOf(form, control);
  return field && field.type === 'text' && !field.upload ? field : undefined;
};
/** Фото товара: все опубликованные слоты фото, главный (без номера) — первым. */
export function photoSlots(form: StudioForm | null | undefined): Field[] {
  const index = (key: string) => Number(key.match(/(\d+)$/)?.[1] ?? 0);
  return (form?.fields ?? [])
    .filter(field => field.type === 'upload' && field.upload?.kind === 'photo')
    .sort((a, b) => index(a.key) - index(b.key) || a.key.localeCompare(b.key));
}
export const defaultOf = (field: Field | undefined): string | undefined => {
  const raw = record(field).default_value;
  return typeof raw === 'string' && field && (!field.values.length || field.values.includes(raw)) ? raw : undefined;
};
/** Значение по умолчанию: опубликованное умолчание, затем умолчание спецификации, если оно опубликовано. */
export function initialChoice(field: Field | undefined, preferred: string[]): string | undefined {
  if (!field) return undefined;
  return defaultOf(field) ?? preferred.find(value => field.values.includes(value)) ?? field.values[0];
}
/** Ключи формы, которые этот экран умеет показать. Неизвестное обязательное поле делает форму неподдерживаемой. */
export function knownKeys(form: StudioForm, controls: ControlKey[]): Set<string> {
  const keys = new Set<string>(photoSlots(form).map(field => field.key));
  for (const control of controls) {
    const field = fieldOf(form, control);
    if (field) keys.add(field.key);
  }
  return keys;
}
export function unsupportedFields(form: StudioForm, controls: ControlKey[]): Field[] {
  const known = knownKeys(form, controls);
  return form.fields.filter(field => !known.has(field.key) && field.required);
}

/* ── Подписи к опубликованным значениям (спецификация экранов; не данные заказа) ── */
export interface Choice {
  name: string;
  sub?: string;
  short?: string;
  badge?: string;
  themes?: string[];
  ratio?: string;
  size?: string;
}
type Dict = Record<string, Choice>;
export const label = (dict: Dict, value: string | undefined) => (value ? (dict[value]?.name ?? value) : '');

/** Площадки (§1.5, §13.1): размер и пропорции; Ozon Fresh — только у «Слайдов», обложка без надписей. */
export const PLATFORMS: Dict = {
  wb: { name: 'Wildberries', size: '1200 × 1600', ratio: '3:4', sub: '1200 × 1600 · без надписей на фото' },
  ozon: { name: 'Ozon', size: '1200 × 1600', ratio: '3:4', sub: '1200 × 1600 · без надписей на фото' },
  ym: { name: 'Яндекс Маркет', short: 'Я.Маркет', size: '1200 × 1600', ratio: '3:4', sub: '1200 × 1600 · без надписей на фото' },
  fresh: { name: 'Ozon Fresh', size: '1200 × 1200', ratio: '1:1', sub: '1200 × 1200 · обложка без надписей' },
};
export const platformCell = (value: string | undefined) => (value ? (PLATFORMS[value]?.short ?? label(PLATFORMS, value)) : '');

/** 11 оформлений «Слайдов с преимуществами» (§13.1). */
export const INFOG_STYLES: Dict = {
  min: { name: 'Минимал', sub: 'Светлый фон, тонкий шрифт и много воздуха', themes: ['Электроника', 'Дом', 'Красота'], badge: 'Хит' },
  prem: { name: 'Премиум', sub: 'Графит, светлый текст и золотой акцент', themes: ['Электроника', 'Красота'] },
  eco: { name: 'Эко', sub: 'Крафт, бежевый и приглушённый зелёный', themes: ['Красота', 'Еда', 'Дом'] },
  bright: { name: 'Яркий', sub: 'Насыщенный фон и крупные плашки с цифрами', themes: ['Электроника', 'Детское', 'Спорт'] },
  tech: { name: 'Технический', sub: 'Сетка, выноски и моноширинные цифры', themes: ['Электроника'] },
  pastel: { name: 'Пастель', sub: 'Мягкие градиенты и глянцевый подиум', themes: ['Красота'], badge: 'Новое' },
  kids: { name: 'Детский', sub: 'Круглые формы, тёплый фон и крупные значки', themes: ['Детское'] },
  sport: { name: 'Спорт', sub: 'Диагонали, жирный курсив и контраст', themes: ['Спорт', 'Одежда'] },
  mag: { name: 'Журнал', sub: 'Крупная антиква и свободная вёрстка', themes: ['Одежда', 'Красота'], badge: 'Новое' },
  scandi: { name: 'Сканди', sub: 'Светлое дерево и мягкие тени', themes: ['Дом', 'Еда'] },
  glass: { name: 'Стекло', sub: 'Цветная сцена и прозрачные плашки', themes: ['Электроника', 'Красота'], badge: 'Новое' },
};
export const INFOG_THEMES = ['Электроника', 'Красота', 'Дом', 'Одежда', 'Детское', 'Спорт', 'Еда'];

/** 10 оформлений «Карусели для поста» (§14): и ключ шаблона, и номер прототипа. */
const POST_STYLE_LIST: Array<[string, string, Choice]> = [
  ['1', 'min', { name: 'Минимал', sub: 'Белый фон и крупный чёрный шрифт', themes: ['Текст'], badge: 'Хит' }],
  ['2', 'dark', { name: 'Тёмный', sub: 'Чёрный фон, светлый текст и один акцент', themes: ['Текст'] }],
  ['3', 'shot', { name: 'Пост-скриншот', sub: 'Слайд как пост в соцсети: аватар, имя, текст', themes: ['Текст', 'Истории'], badge: 'Новое' }],
  ['4', 'note', { name: 'Заметки', sub: 'Лист в линейку и выделение маркером', themes: ['Текст', 'Списки'] }],
  ['5', 'mag', { name: 'Журнал', sub: 'Фото и антиква, подписи как в журнале', themes: ['С фото', 'Истории'] }],
  ['6', 'photo', { name: 'Фото на весь слайд', sub: 'Ваши фото и текст на плашке', themes: ['С фото'] }],
  ['7', 'blocks', { name: 'Цветные блоки', sub: 'У каждого слайда свой цвет и крупная цифра', themes: ['Списки'] }],
  ['8', 'check', { name: 'Чек-лист', sub: 'Шаги с галочками и полоса прогресса', themes: ['Списки'] }],
  ['9', 'pano', { name: 'Бесшовная панорама', sub: 'Фон и линия перетекают со слайда на слайд', themes: ['Текст', 'Истории'], badge: 'Новое' }],
  ['10', 'quote', { name: 'Цитата и история', sub: 'Крупная цитата, портрет и рассказ от первого лица', themes: ['С фото', 'Истории'] }],
];
export const POST_STYLES: Dict = Object.fromEntries(POST_STYLE_LIST.flatMap(([id, key, value]) => [[id, value], [key, value]]));
export const POST_THEMES = ['Текст', 'С фото', 'Списки', 'Истории'];
/** Ключ оформления для CSS предпросмотра: номер прототипа сводится к ключу шаблона. */
export const postStyleKey = (value: string | undefined) => POST_STYLE_LIST.find(([id, key]) => id === value || key === value)?.[1] ?? 'min';

export const POST_FORMATS: Dict = {
  '4:5': { name: 'Пост', sub: 'Instagram · 1080 × 1350', ratio: '4:5' },
  '1:1': { name: 'Квадрат', sub: 'ВКонтакте и Telegram · 1080 × 1080', ratio: '1:1' },
};
export const POST_TONES: Dict = {
  friendly: { name: 'Дружелюбный', sub: 'Как другу за кофе' },
  expert: { name: 'Экспертный', sub: 'Факты и цифры без воды' },
  short: { name: 'Лаконичный', sub: 'Коротко и по делу' },
  bold: { name: 'Дерзкий', sub: 'Смело и с вызовом' },
  inspire: { name: 'Вдохновляющий', sub: 'Заряжает начать' },
  fun: { name: 'С юмором', sub: 'Легко и с улыбкой' },
};
export const POST_CHIPS = ['Ошибки в беге', 'Мифы о сне', 'Своя кофейня', 'Перед отпуском', 'Завтраки', 'Гардероб'];

/** Варианты работ «Карточки для маркетплейса» (§11, п. 3). */
export const MCARD_SETS: Dict = {
  cat: { name: 'Каталог', sub: 'Белый фон и ракурсы' },
  model: { name: 'С моделью', sub: 'Товар в руках и на человеке' },
  life: { name: 'Лайфстайл', sub: 'Стол, гостиная, кафе' },
  prem: { name: 'Премиум', sub: 'Травертин и шёлк', badge: 'Новое' },
  min: { name: 'Минимал', sub: 'Белый и светло-серый' },
  pastel: { name: 'Пастель', sub: 'Цветной подиум' },
  nat: { name: 'Природа', sub: 'Горы и вода' },
  city: { name: 'Город', sub: 'Улица и кафе' },
  ny: { name: 'Новый год', sub: 'Ель и гирлянды', badge: 'Новое' },
  gift: { name: 'Подарок', sub: 'Коробка и бархат' },
  sport: { name: 'Спорт', sub: 'Зал и пробежка' },
  dark: { name: 'Тёмная', sub: 'Графит и подсветка' },
  own: { name: 'Свой набор', sub: 'Сцены на выбор' },
};
export const MCARD_MOTIONS: Dict = {
  '101': { name: 'Вращение', sub: 'Товар плавно поворачивается', badge: 'Хит' },
  '102': { name: 'Наезд камеры', sub: 'Камера медленно приближается' },
  '103': { name: 'Блик света', sub: 'По товару проходит мягкий блик', badge: 'Новое' },
  '104': { name: 'Парение', sub: 'Товар висит в воздухе и чуть покачивается' },
  '105': { name: 'Сияние', sub: 'Мягкий свет и частицы вокруг' },
  '106': { name: 'Студия', sub: 'Чистый фон и ровный свет' },
  '107': { name: 'Отъезд камеры', sub: 'Камера плавно отдаляется от товара' },
  '108': { name: 'Капли воды', sub: 'Брызги и капли на свету', badge: 'Новое' },
  '109': { name: 'Подиум', sub: 'Товар поворачивается на подиуме' },
  '110': { name: 'Смена сцен', sub: 'Три сцены одна за другой' },
};
export const DESC_TONES: Dict = {
  sell: { name: 'Продающий', badge: 'Хит' },
  expert: { name: 'Экспертный' },
  neutral: { name: 'Нейтральный' },
  prem: { name: 'Премиальный' },
  friend: { name: 'Дружелюбный' },
  short: { name: 'Коротко' },
  gift: { name: 'Подарочный', badge: 'Новое' },
  seo: { name: 'Для поиска' },
};
export const MCARD_WISHES = ['Мягкий свет', 'Без реквизита', 'Тёплые тона', 'Больше воздуха'];
export const SPEC_KEYS = ['Материал', 'Размер', 'Вес', 'Цвет', 'Комплектация', 'Гарантия', 'Страна'];

/* ── Текст «Название и преимущества» ── */
/** Первая строка — название, дальше по преимуществу на строку (§11 п. 4, §13.1 п. 4). */
export function splitProductText(value: string): { name: string; advantages: string[] } {
  const lines = value
    .split('\n')
    .map(line => line.replace(/^\s*(?:[-–—•*]|\d+[.)])\s*/, '').trim())
    .filter(Boolean);
  return { name: lines[0] ?? '', advantages: lines.slice(1) };
}
export const joinProductText = (name: string, advantages: string[]) => [name, ...advantages].map(line => line.trim()).filter(Boolean).join('\n');

/* ── Характеристики «Слайдов» (до 8 пар «ключ · значение») ── */
export type SpecRow = [string, string];
export function parseSpecs(value: string | undefined): SpecRow[] {
  return (value ?? '')
    .split('\n')
    .map(line => {
      const at = line.indexOf(':');
      return (at < 0 ? [line.trim(), ''] : [line.slice(0, at).trim(), line.slice(at + 1).trim()]) as SpecRow;
    })
    .filter(([key, text]) => key || text)
    .slice(0, 8);
}
export const formatSpecs = (rows: SpecRow[]) =>
  rows
    .slice(0, 8)
    .map(([key, value]) => `${key.slice(0, 24)}: ${value.slice(0, 24)}`)
    .join('\n');

/* ── Стоп-слова площадок (§13.1 п. 4): проверка предупреждает и не запрещает создать ── */
const RULES: Array<{ group: string; source: string; only?: string[] }> = [
  {
    group: 'цены, скидки',
    source:
      '\\d[\\d\\s]*(?:₽|руб\\.?|р\\.)|цена|цены|цене|цену|ценой|ценник\\p{L}*|скидк\\p{L}*|распродаж\\p{L}*|акци(?:я|и|ю|ей)|дешевле|кешб[еэ]к\\p{L}*|кэшб[еэ]к\\p{L}*|[-−–]\\s?\\d+\\s?%',
  },
  {
    group: 'превосходные степени',
    source: 'лучш\\p{L}*|идеальн\\p{L}*|сам(?:ый|ая|ое|ые|ого|ой|ых)|№\\s?1|номер один|топ(?:[-\\s]?\\d+| продаж\\p{L}*)|в топе|топов\\p{L}*|хит|лидер продаж',
  },
  {
    group: 'призывы',
    source: 'купи\\p{L}*|закаж\\p{L}*|заказыва\\p{L}*|успе(?:й|йте)|звони\\p{L}*|пиши(?:те)?|переходи(?:те)?|жми(?:те)?|добав(?:ь|ьте) в корзину|подпиш\\p{L}*|только сегодня',
  },
  {
    group: 'ссылки и контакты',
    source:
      'https?:\\/\\/\\S+|www\\.\\S+|[\\p{L}\\d-]+\\.(?:ru|com|рф|net|org|shop)|t\\.me\\/\\S+|@[a-z0-9_]{3,}|\\+7[\\s\\d()-]{9,}|whatsapp|telegram|телеграм\\p{L}*|instagram|инстаграм\\p{L}*|вконтакт\\p{L}*',
  },
  { group: 'QR-код', source: 'qr(?:-код\\p{L}*)?|кьюар\\p{L}*' },
  { group: 'доставку и возврат', source: 'доставк\\p{L}*|возврат\\p{L}*' },
  { group: 'заявления об оригинальности', source: 'оригинал(?:а|ом|у|ы)?|аналог(?:а|ов|и|ам)?|подделк\\p{L}*' },
  { group: 'обещания', source: 'вернём деньги|вернем деньги|товар закончился|скоро в продаже|за отзыв' },
  { group: 'сравнения', source: 'лучше чем|в отличие от|по сравнению с', only: ['ym'] },
];
const compiled = RULES.map(rule => ({ ...rule, re: new RegExp(`(^|[^\\p{L}\\p{N}])(${rule.source})(?=$|[^\\p{L}\\p{N}])`, 'giu') }));
export function stopWords(texts: string[], platform: string | undefined) {
  const words: string[] = [],
    groups: string[] = [],
    lines: number[] = [];
  texts.forEach((value, line) => {
    for (const rule of compiled) {
      if (rule.only && !rule.only.includes(platform ?? '')) continue;
      rule.re.lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = rule.re.exec(value))) {
        const word = match[2].trim();
        if (word && !words.some(item => item.toLowerCase() === word.toLowerCase())) words.push(word);
        if (!groups.includes(rule.group)) groups.push(rule.group);
        if (!lines.includes(line)) lines.push(line);
        if (!match[0].length) rule.re.lastIndex++;
      }
    }
  });
  return { words, groups, lines };
}
export function andList(items: string[], max: number, quote = false) {
  const shown = items.slice(0, max).map(item => (quote ? `«${item}»` : item));
  if (items.length > max) shown.push(`ещё ${items.length - max}`);
  return shown.length > 1 ? `${shown.slice(0, -1).join(', ')} и ${shown[shown.length - 1]}` : (shown[0] ?? '');
}

/* ── Мелочи подписи ── */
export function plural(value: number, forms: [string, string, string]) {
  const n = Math.abs(value) % 100,
    m = n % 10;
  return forms[n > 10 && n < 20 ? 2 : m === 1 ? 0 : m >= 2 && m <= 4 ? 1 : 2];
}
export const count = (value: string | number, forms: [string, string, string]) => `${value} ${plural(Number(value), forms)}`;
export const SLIDE_FORMS: [string, string, string] = ['слайд', 'слайда', 'слайдов'];
export const PHOTO_FORMS: [string, string, string] = ['фото', 'фото', 'фото'];
export const ROW_FORMS: [string, string, string] = ['строка', 'строки', 'строк'];
/** Длина в UTF-16 — так считает Backend (length_unit: utf16). */
export const utf16 = (value: string | undefined) => (value ?? '').length;
export const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
